import { z } from "zod";

/**
 * A market: a venue, a currency, a timezone, and a calendar that says when it
 * is open.
 *
 * Until now every price in this product was invented, and `SyntheticProvider`
 * declares `marketHoursOnly: false` — which is the whole reason the platform is
 * demonstrable on a Sunday. That convenience shaped the domain: there was no
 * venue, no session, and `trading_accounts.base_currency` existed with nothing
 * reading it. This module is where "which venue is this, what does it settle
 * in, and is it open right now" becomes a question the system can answer from a
 * pure function, before anything is asked to depend on the answer.
 *
 * **No I/O, no clock of its own.** `sessionAt` takes the instant it is asked
 * about. Everything here is a function of its arguments, which is what lets the
 * backtest harness ask about a Tuesday in 2019 and get the same answer twice.
 */

/**
 * The markets this product knows about.
 *
 * `US` is the New York trading day. `SIM` is the always-open simulated venue
 * that the synthetic and replay providers serve, and it is a market rather than
 * a flag because "no market" is not a thing an order can belong to.
 *
 * Egypt was the original second market and is deferred: free EGX data does not
 * exist, and this milestone does not promise a market it cannot feed. Adding it
 * later is a calendar and a provider, which is exactly the shape this file is
 * built to accept.
 */
export const MARKET_IDS = ["US", "SIM"] as const;
export type MarketId = (typeof MARKET_IDS)[number];

export const marketIdSchema = z.enum(MARKET_IDS);

/** What a market is doing at a given instant. */
export const MARKET_STATES = ["open", "closed", "unknown"] as const;
export type MarketState = (typeof MARKET_STATES)[number];

export const marketStateSchema = z.enum(MARKET_STATES);

/**
 * One trading window, as local wall-clock minutes from midnight.
 *
 * Minutes rather than "09:30" so that comparing them is arithmetic rather than
 * string parsing, and `end` is exclusive so two adjacent windows cannot both
 * claim the same minute.
 */
type Window = { openMinute: number; closeMinute: number };

/** 09:30 to 16:00 New York time, the regular session. */
const US_REGULAR: Window = { openMinute: 9 * 60 + 30, closeMinute: 16 * 60 };

/** 09:30 to 13:00, the early close before a holiday. */
const US_HALF_DAY: Window = { openMinute: 9 * 60 + 30, closeMinute: 13 * 60 };

/** Midnight to midnight. A day with no closure, expressed as a window like any other. */
const ALL_DAY: Window = { openMinute: 0, closeMinute: 24 * 60 };

export type Market = {
  id: MarketId;
  /** For a human: "US equities (NYSE/Nasdaq)". */
  name: string;
  /** ISO 4217. The currency an account on this market settles in. */
  currency: string;
  /** An IANA zone. Passed to `Intl`, never used to compute an offset here. */
  timeZone: string;
  /** Which weekdays trade, 0 = Sunday. */
  tradingDays: readonly number[];
  /** The window on an ordinary trading day. */
  regular: Window;
  /**
   * Days the market is shut, as `YYYY-MM-DD` in the market's own timezone.
   *
   * **Enumerated, not computed, and that is deliberate.** Good Friday moves
   * with the ecclesiastical calendar, Juneteenth and Independence Day shift when
   * they fall at a weekend — with New Year's Day taking the opposite exception —
   * and the NYSE has closed for national days of mourning that no recurrence
   * rule predicts (9 January 2025, for President Carter, is in this list for
   * that reason). A clever rule would be wrong on a schedule nobody is
   * watching; a hand-kept list with a stated horizon is wrong in a way that
   * announces itself.
   */
  holidays: ReadonlySet<string>;
  /** Days that close early, same date format. */
  halfDays: ReadonlySet<string>;
  /**
   * The last date the calendar speaks for, `YYYY-MM-DD`.
   *
   * Past this, `sessionAt` answers `unknown`. A calendar that silently keeps
   * applying last year's holidays is a calendar that is confidently wrong, and
   * an `unknown` a caller must handle is the only version of this that gets
   * noticed before a trade depends on it.
   */
  horizon: string;
};

/**
 * NYSE and Nasdaq holidays, 2025 through 2030.
 *
 * Source: the NYSE holiday calendar. Kept by hand; extend it and move
 * `horizon` in the same change, or the extension is invisible.
 */
const US_HOLIDAYS = new Set([
  // 2025
  "2025-01-01", // New Year's Day
  "2025-01-09", // national day of mourning — President Carter
  "2025-01-20", // Martin Luther King Jr. Day
  "2025-02-17", // Washington's Birthday
  "2025-04-18", // Good Friday
  "2025-05-26", // Memorial Day
  "2025-06-19", // Juneteenth
  "2025-07-04", // Independence Day
  "2025-09-01", // Labor Day
  "2025-11-27", // Thanksgiving
  "2025-12-25", // Christmas
  // 2026
  "2026-01-01",
  "2026-01-19",
  "2026-02-16",
  "2026-04-03", // Good Friday
  "2026-05-25",
  "2026-06-19",
  "2026-07-03", // Independence Day observed — the 4th is a Saturday
  "2026-09-07",
  "2026-11-26",
  "2026-12-25",
  // 2027
  "2027-01-01",
  "2027-01-18",
  "2027-02-15",
  "2027-03-26", // Good Friday
  "2027-05-31",
  "2027-06-18", // Juneteenth observed — the 19th is a Saturday
  "2027-07-05", // Independence Day observed — the 4th is a Sunday
  "2027-09-06",
  "2027-11-25",
  "2027-12-24", // Christmas observed — the 25th is a Saturday
  // 2028 — no New Year's holiday: 1 January is a Saturday, and New Year's Day
  // is the one holiday the NYSE does NOT move back into the previous year.
  "2028-01-17",
  "2028-02-21",
  "2028-04-14", // Good Friday
  "2028-05-29",
  "2028-06-19",
  "2028-07-04",
  "2028-09-04",
  "2028-11-23",
  "2028-12-25",
  // 2029
  "2029-01-01",
  "2029-01-15",
  "2029-02-19",
  "2029-03-30", // Good Friday
  "2029-05-28",
  "2029-06-19",
  "2029-07-04",
  "2029-09-03",
  "2029-11-22",
  "2029-12-25",
  // 2030
  "2030-01-01",
  "2030-01-21",
  "2030-02-18",
  "2030-04-19", // Good Friday
  "2030-05-27",
  "2030-06-19",
  "2030-07-04",
  "2030-09-02",
  "2030-11-28",
  "2030-12-25",
]);

/** Early closes at 13:00 ET: the day after Thanksgiving, and the eves of two holidays. */
const US_HALF_DAYS = new Set([
  "2025-07-03",
  "2025-11-28",
  "2025-12-24",
  "2026-11-27",
  "2026-12-24",
  "2027-11-26",
  "2028-07-03",
  "2028-11-24",
  "2029-07-03",
  "2029-11-23",
  "2029-12-24",
  "2030-07-03",
  "2030-11-29",
  "2030-12-24",
]);

const MARKETS: Record<MarketId, Market> = {
  US: {
    id: "US",
    name: "US equities",
    currency: "USD",
    timeZone: "America/New_York",
    tradingDays: [1, 2, 3, 4, 5],
    regular: US_REGULAR,
    holidays: US_HOLIDAYS,
    halfDays: US_HALF_DAYS,
    horizon: "2030-12-31",
  },

  /**
   * The simulated venue.
   *
   * Seven trading days, a window covering the whole of each of them, no
   * holidays — and therefore open at every instant, **without a line of code
   * anywhere asking whether the market is SIM**. That is the point. A branch
   * would pass every test written here and quietly guarantee that the next
   * always-open market is a second special case; a calendar composes.
   *
   * Its horizon is far enough out that a backtest over historical data does not
   * meet it. It is not infinite, because "this calendar speaks for all time" is
   * a claim no calendar should make.
   */
  SIM: {
    id: "SIM",
    name: "Simulated venue",
    currency: "USD",
    timeZone: "UTC",
    tradingDays: [0, 1, 2, 3, 4, 5, 6],
    regular: ALL_DAY,
    holidays: new Set<string>(),
    halfDays: new Set<string>(),
    horizon: "2099-12-31",
  },
};

export function market(id: MarketId): Market {
  return MARKETS[id];
}

export function allMarkets(): readonly Market[] {
  return MARKET_IDS.map((id) => MARKETS[id]);
}

export type Session = {
  market: MarketId;
  state: MarketState;
  /** The local date the instant fell on, in the market's timezone. `YYYY-MM-DD`. */
  localDate: string;
  /** When the current or next session opens, if the calendar can say. */
  opensAt?: Date | undefined;
  /** When the current session closes, present only while `state` is `open`. */
  closesAt?: Date | undefined;
  /** Why it is shut, when it is: `weekend`, `holiday`, `outside_hours`, `beyond_horizon`. */
  reason?: "weekend" | "holiday" | "outside_hours" | "beyond_horizon";
};

/**
 * Is this market open at this instant, and when does that next change?
 *
 * Pure, and told the instant rather than reading a clock — so a backtest asking
 * about a Tuesday in 2019 gets the same answer every time it asks.
 */
export function sessionAt(id: MarketId, at: Date): Session {
  const m = MARKETS[id];
  const localDate = localDateOf(at, m.timeZone);

  if (localDate > m.horizon) {
    // Past what the calendar was written for. Guessing here produces a wrong
    // "open" or a wrong "closed", and both are worse than saying so.
    return { market: id, state: "unknown", localDate, reason: "beyond_horizon" };
  }

  const window = windowFor(m, localDate);
  if (!window) {
    return {
      market: id,
      state: "closed",
      localDate,
      reason: m.holidays.has(localDate) ? "holiday" : "weekend",
      opensAt: nextOpen(m, localDate),
    };
  }

  const minute = localMinuteOf(at, m.timeZone);
  const opensAt = instantAt(m, localDate, window.openMinute);
  const closesAt = instantAt(m, localDate, window.closeMinute);

  if (minute < window.openMinute) {
    return { market: id, state: "closed", localDate, reason: "outside_hours", opensAt };
  }
  if (minute >= window.closeMinute) {
    return {
      market: id,
      state: "closed",
      localDate,
      reason: "outside_hours",
      opensAt: nextOpen(m, localDate),
    };
  }

  return { market: id, state: "open", localDate, opensAt, closesAt };
}

/**
 * Shorthand for the common question.
 *
 * `marketIsOpen`, not `isOpen`: `order.ts` already exports an `isOpen(status)`,
 * and two functions of that name in one barrel is a coin flip at every call
 * site — the sort of ambiguity that reads fine and means the wrong thing.
 */
export function marketIsOpen(id: MarketId, at: Date): boolean {
  return sessionAt(id, at).state === "open";
}

/**
 * The exclusive end, as a local date, of a daily history that holds only the
 * sessions that had closed `settleMs` before `at` (`M10/E4`).
 *
 * The local date after `at` once that date's session has closed and settled;
 * otherwise `at`'s own local date, so a session still trading, or one closed
 * too recently to trust its bar, is never included. A weekend or a holiday has
 * no session, so its own date is already past every closed one. Asked in the
 * market's timezone, never in UTC: a job started hours late, past midnight UTC,
 * still lands on the session that closed rather than on a date nobody traded.
 * Half-days close at 13:00 and count from then.
 */
export function closedSessionsEnd(id: MarketId, at: Date, settleMs = 0): string {
  const m = MARKETS[id];
  const settled = new Date(at.getTime() - settleMs);
  const localDate = localDateOf(settled, m.timeZone);
  const window = windowFor(m, localDate);
  const closed = window !== undefined && localMinuteOf(settled, m.timeZone) >= window.closeMinute;
  return closed ? addDays(localDate, 1) : localDate;
}

// ── the timezone, asked rather than computed ────────────────────────────────

/**
 * `Intl` does the timezone, here and nowhere else.
 *
 * New York is UTC−5 or UTC−4 by a rule that has been changed by act of Congress
 * twice in living memory, and any offset table written here would be a stale
 * copy of one the runtime already ships and maintains. `Intl.DateTimeFormat` is
 * available in Workers, in Node and in the browser, so the whole DST question
 * reduces to "what does the local wall clock read" — which is the thing the
 * calendar is actually about.
 *
 * The formatters are built once per zone. Constructing one costs more than the
 * formatting, and this runs on the hot path of every quote.
 */
const FORMATTERS = new Map<string, Intl.DateTimeFormat>();

function formatterFor(timeZone: string): Intl.DateTimeFormat {
  let f = FORMATTERS.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
    });
    FORMATTERS.set(timeZone, f);
  }
  return f;
}

function partsOf(at: Date, timeZone: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const part of formatterFor(timeZone).formatToParts(at)) out[part.type] = part.value;
  return out;
}

/** `YYYY-MM-DD` as the market's own calendar reads it at this instant. */
export function localDateOf(at: Date, timeZone: string): string {
  const p = partsOf(at, timeZone);
  return `${p.year}-${p.month}-${p.day}`;
}

/** Minutes since local midnight, in the market's timezone. */
function localMinuteOf(at: Date, timeZone: string): number {
  const p = partsOf(at, timeZone);
  return Number(p.hour) * 60 + Number(p.minute);
}

/**
 * The UTC instant at which a market's local date reads a given minute.
 *
 * Found by search rather than by arithmetic: guess with a fixed offset, ask
 * `Intl` what that guess actually reads, and correct by the difference. Two
 * rounds settle it including across a DST transition, and it needs no offset
 * table — which is the property the whole module is built around.
 *
 * **The one case it cannot settle**, stated rather than hidden: a local time
 * that does not exist, because the clocks jumped over it. 02:30 on a spring
 * forward Sunday is not an instant, and no function can return one. The loop is
 * bounded rather than `while`, so it returns the nearest real instant instead of
 * oscillating. No session here opens in that window — New York jumps at 02:00
 * and opens at 09:30, and SIM is UTC, which has no transitions — so this is a
 * property of the code rather than a bug anyone can reach today. A market that
 * opened at 02:30 would need this looked at.
 */
function instantAt(m: Market, localDate: string, minute: number): Date {
  const [y, mo, d] = localDate.split("-").map(Number);
  // A first guess that treats the wall clock as UTC. Wrong by the zone's
  // offset, which the correction below measures rather than assumes.
  let guess = Date.UTC(y as number, (mo as number) - 1, d as number, 0, minute);

  for (let i = 0; i < 2; i++) {
    const p = partsOf(new Date(guess), m.timeZone);
    const reads =
      Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day)) +
      Number(p.hour) * 3_600_000 +
      Number(p.minute) * 60_000;
    const wanted = Date.UTC(y as number, (mo as number) - 1, d as number) + minute * 60_000;
    if (reads === wanted) break;
    guess += wanted - reads;
  }

  return new Date(guess);
}

// ── the calendar ────────────────────────────────────────────────────────────

/** The window this market trades on that local date, or nothing if it is shut. */
function windowFor(m: Market, localDate: string): Window | undefined {
  if (m.holidays.has(localDate)) return undefined;
  if (!m.tradingDays.includes(weekdayOf(localDate))) return undefined;
  return m.halfDays.has(localDate) ? US_HALF_DAY : m.regular;
}

/** 0 = Sunday. From the date string, so no timezone is involved. */
function weekdayOf(localDate: string): number {
  const [y, mo, d] = localDate.split("-").map(Number);
  return new Date(Date.UTC(y as number, (mo as number) - 1, d as number)).getUTCDay();
}

/**
 * The next open after this local date, searched forward a day at a time.
 *
 * Bounded by the horizon, and undefined past it — a market whose calendar has
 * run out cannot say when it opens next, and inventing a date would be the same
 * lie `state: "unknown"` exists to avoid. The longest real gap is a weekend plus
 * a holiday, so the loop is short; the bound is against a calendar that ends.
 */
function nextOpen(m: Market, fromLocalDate: string): Date | undefined {
  let date = fromLocalDate;
  for (let i = 0; i < 370; i++) {
    date = addDays(date, 1);
    if (date > m.horizon) return undefined;
    const window = windowFor(m, date);
    if (window) return instantAt(m, date, window.openMinute);
  }
  return undefined;
}

function addDays(localDate: string, days: number): string {
  const [y, mo, d] = localDate.split("-").map(Number);
  const next = new Date(Date.UTC(y as number, (mo as number) - 1, (d as number) + days));
  return next.toISOString().slice(0, 10);
}
